import gql from 'graphql-tag';
import Router from 'next/router';
import React, { useContext } from 'react';
import styled from 'styled-components';
import Button from '@components/buttons';
// import Grid from '@components/Grid';
import { Box, Container } from '@components/layout/Grid';
import MetaTags from '@components/MetaTags';
import SectionHeader from '@components/SectionHeader';
import LanguageContext from '@lib/contexts/languageContext';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';
import CollectionCard from '@organisms/cards/CollectionCard';
import PlaylistCard from '@organisms/cards/PlaylistCard';
import Carousel from '@organisms/Carousel';
import CardRowSkeleton from '@skeletons/CardRowSkeleton';
import CollectionRowSkeleton from '@skeletons/CollectionRowSkeleton';
import { UserContext } from '@lib/contexts/UserProvider';
import { useQuery } from '@apollo/client';
import EmptyResults from '@components/EmptyResults';

const COLLECTIONS_ID = '02e12572-1156-46c7-9c0c-b05f7a7c745d';

const GET_PLAYLISTS = gql`
  query playlists($collection: String) {
    playlists(collection: $collection) {
      data {
        id
        identifier
        title
        description
        visibility
        owner {
          id
          firstName
        }
        meta {
          videoCount
          minutes
        }
        assets {
          thumbnailUrl
        }
      }
    }
  }
`;

const GET_COLLECTIONS = gql`
  query taxonomies($filter: String) {
    taxonomies(filter: $filter) {
      terms {
        id
        name
        titleImageUrl
        slug
      }
    }
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  grid-gap: 10px;
`;

const SamplePlaylists = () => {
  const [currentUser] = useContext(UserContext);
  const locale = useContext(LanguageContext);
  const { loading, data, error } = useQuery(GET_PLAYLISTS, {
    variables: { collection: 'recommended-playlists' },
    fetchPolicy: 'network-only',
  });

  if (error) {
    return <EmptyResults icon={'ygb-icon-playlist-empty-ic'} />;
  } else if (loading) {
    return <CardRowSkeleton items={[1, 2, 3]} margin="0" overviewsection />;
  }

  const { playlists } = data;
  return (
    <Box mb="30px">
      <Box mb={['15px', '15px', '15px']} px={[15, 20, 20]}>
        <SectionHeader
          title={<Trans id="playlists.get_started" message={'Get Started'} />}
          button={
            currentUser?.id ? (
              <Button
                appearance={'link'}
                onClick={() => {
                  Router.push(
                    `/[lang]/me/playlists`,
                    `/${locale}/me/playlists`
                  );
                }}
              >
                <Trans
                  id="playlists.view_my_playlist"
                  message={'View My Playlists'}
                />
              </Button>
            ) : null
          }
        />
      </Box>
      <Box mb={15} pr={[0, 0, 20]} pl={[0, 0, 20]}>
        <Carousel itemPerView={3}>
          {playlists.data.map(playlist => (
            <PlaylistCard
              key={playlist.id}
              playlist={{
                id: playlist.id,
                identifier: playlist.identifier,
                visibility: playlist.visibility,
                title: playlist.title,
                description: playlist.description,
                thumbnailUrl: playlist.assets.thumbnailUrl,
                duration: playlist.meta.minutes,
                videoCount: playlist.meta.videoCount,
                createdBy: playlist.owner.firstName,
                ownerId: playlist.owner.id,
              }}
            />
          ))}
        </Carousel>
      </Box>
    </Box>
  );
};

const PlaylistCollections = () => {
  const locale = useContext(LanguageContext);
  const { error, loading, data } = useQuery(GET_COLLECTIONS, {
    variables: { filter: COLLECTIONS_ID },
    ssr: false,
  });

  if (error) return <EmptyResults icon={'ygb-icon-playlist-empty-ic'} />;
  if (loading) {
    return <CollectionRowSkeleton animation={false} />;
  }
  const { taxonomies } = data;
  return (
    <Box px={[15, 20, 20]}>
      <Box mb={15}>
        <SectionHeader
          title={
            <Trans
              id="playlists.browse_category"
              message={'Browse by category'}
            />
          }
        />
      </Box>
      <Grid>
        {taxonomies[0]?.terms?.map?.(term => (
          <CollectionCard
            thumbnailUrl={term.titleImageUrl}
            name={term.name}
            locale={locale}
            slug={term.slug}
            key={term.slug}
          />
        ))}
      </Grid>
    </Box>
  );
};

const Playlists = () => {
  return (
    <Container flexDirection="column" mt={[25, 30, 40]} mb={[25, 30, 40]}>
      <React.Fragment>
        <MetaTags title={t`opengraph.playlists_title`} />
        <SamplePlaylists />
        <PlaylistCollections />
      </React.Fragment>
    </Container>
  );
};

export default Playlists;
